import { breakpoints, BreakpointName } from "../../breakpoints.ts";

/**
 * Toggle the visibility of an element from a click event on another element (like a button)
 * Use provided breakpoint sizes to determine when the functionality should be active or if the content should be shown
 * and the controlling button hidden.
 */
export class Expander {
	#expander: HTMLElement;
	#expandees: HTMLElement[];
	#sizes: BreakpointName[];
	#isExpandOnce: boolean;
	#mql: MediaQueryList | undefined;

	/**
	 *
	 */
	public constructor(expander: HTMLElement, sizes: BreakpointName[]) {
		this.#expander = expander;
		const expandeeIds = (expander.getAttribute("aria-controls") || "").split(" ").filter(expandeeId => expandeeId.trim());
		this.#expandees = expandeeIds.map(expandeeId => document.getElementById(expandeeId)).filter(el => !!el);
		this.#sizes = sizes;
		this.#isExpandOnce = "expandOnce" in this.#expander.dataset;

		this.#bindEvents();
	}

	/**
	 *
	 */
	#bindEvents(): void {
		this.#expander.addEventListener("click", (event) => {
			event.preventDefault();
			const isExpanded = this.#expander.dataset["expanderExpanded"] === "true" ? "false" : "true";
			this.#expandees.forEach(expandee => expandee.dataset["expanderExpanded"] = isExpanded);

			if (this.#isExpandOnce) {
				this.#expander.setAttribute("aria-hidden", "true");
			}
		});

		const sizes = this.#sizes.filter(size => breakpoints.has(size));

		// build a media query to match the specified sizes
		const mq = sizes.map(size => {
			const breakpointNames = [...breakpoints.keys()];
			const nextBreakpoint = breakpointNames[breakpointNames.indexOf(size) + 1];
			const startWidth = breakpoints.get(size);
			const endWidth = ((nextBreakpoint === undefined ? 0 : breakpoints.get(nextBreakpoint)) || 9999.0002) - 0.0002;

			return `(min-width: ${startWidth}rem) and (max-width: ${endWidth}rem)`;
		}).join(", ");

		// watch the media query for changes (and run once on load)
		this.#mql = matchMedia(mq);
		this.#toggleExpandFunctionality();
		this.#mql.addEventListener("change", this.#toggleExpandFunctionality.bind(this));
	}

	/**
	 *
	 */
	#toggleExpandFunctionality(): void {
		this.#expander.setAttribute("aria-expanded", (!this.#mql?.matches).toString());
		this.#expander.setAttribute("aria-hidden", (!this.#mql?.matches).toString());
		this.#expandees.forEach(expandee => expandee.dataset["expanderExpanded"] = (!this.#mql?.matches).toString());
	}

	/**
	 * Expand the expander content. (Ignore if media query doesn't match)
	 */
	public expand(): void {
		if (this.#mql?.matches) {
			this.#expandees.forEach(expandee => expandee.dataset["expanderExpanded"] = "true");
			if (this.#isExpandOnce) {
				this.#expander.setAttribute("aria-hidden", "true");
			}
		}
	}

	/**
	 * Collapse the expander contnt. (Ignore if media query doesn't match)
	 */
	public collapse(): void {
		if (this.#mql?.matches) {
			this.#expandees.forEach(expandee => expandee.dataset["expanderExpanded"] = "false");
			this.#expander.setAttribute("aria-hidden", "false"); // bring back the expander trigger even if 'isExpandOnce' set
		}
	}
}
